import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ListState } from "@/types/list";

import { RootState } from "@/redux";

import { Order } from "@/types/types";

import { AsyncProcessingStatus, AsyncProcessingType } from "@/enum/list.enum";

import {
	CopywritingStatus,
	GenerateCopywritingResponse,
} from "../../pages/Messages/components/GenerateBar/types";
import { Conversation, People } from "../../pages/Apollo/schema/get-people";
import { List, ListPerson } from "../../pages/List/schema/list";
import {
	inboxSchedulePatchMatcher,
	patchInboxSchedule,
	toggleInboxSchedules,
} from "./list-slice.schedule.helper";
import { resolveInboxSorting } from "./list-sorting.helper";
import { ColumnValueInfo } from "../../pages/List/schema/columnValue";
import { columnApi } from "../column/column.api";
import { conversationApi } from "../conversation/conversation.api";
import { copywritingOrchestrationApi } from "../copywritingOrchestration/copywritingOrchestration.api";
import { hubspotApi } from "../hubspot/hubspot.api";
import { pipedriveApi } from "../pipedrive/pipedrive.api";
import { scheduleApi } from "../schedule/schedule.api";
import { zohoApi } from "../zoho/zoho.api";
import { CrmProspectUpdate } from "../crm/crm.schema";
import { applyCrmUpdateToListPersons } from "../crm/crmRealtimeUpdate";
import { listApi } from "./list.api";
import { hydrateListPersonCollectionsWithCrmDeal } from "./listCrmDealHydration";
import { applyProspectUpdateToRows, mergeEmailFacts } from "./listProspectUpdate";
import {
	applyGetAllPersonsFulfilled,
	applyGetAllProspectsFulfilled,
	applyGetAllProspectsPending,
	applyGetAllProspectsRejected,
	applyGetListFulfilled,
	applyGetUnassignedAndDummyFulfilled,
	applyGetUnassignedAndDummyPending,
	applyGetUnassignedFulfilled,
	applyGetUnassignedPending,
	applyInboxCleared,
	applyInboxScopeReleased,
	applyLeadContactFound,
	applyListPersonCampaignUpdate,
	applyListPersonSendersUpdate,
	applySingleListPersonToInbox,
	removeLeadFromInboxState,
} from "./list-inbox.helper";
import { addLeanListsOverviewMatchers } from "./list-lean-lists.helper";

type DeepResearchResponseData = NonNullable<People["deepResearchResponse"]>["response"];

const initialState: ListState = {
	lists: [],
	listMeta: {
		page: 1,
		limit: 15,
		itemCount: 15,
		pageCount: 1,
		hasPreviousPage: false,
		hasNextPage: false,
	},
	currentList: null,
	currentFullList: null,
	inbox: [],
	searchResults: [],
	searchResultsMeta: {
		page: 1,
		limit: 15,
		itemCount: 15,
		pageCount: 1,
		hasPreviousPage: false,
		hasNextPage: false,
	},
	currentSearchResultsIndex: 0,
	campaign: [],
	meta: {
		page: 1,
		limit: 15,
		itemCount: 15,
		pageCount: 1,
		hasPreviousPage: false,
		hasNextPage: false,
	},
	sortingList: {
		sortBy: "latestUpdateAt",
		sortOrder: Order.DESC,
	},
	sortingInbox: {
		sortBy: "updatedAt",
		sortOrder: Order.DESC,
	},
	currentLeadIndex: 0,
	leavingInboxLeadId: null,
	scoreCompanyCategories: [],
};

const upsertColumnValueOnPerson = (
	person: ListPerson,
	listPersonId: number,
	columnValue: Pick<
		ColumnValueInfo,
		"columnConfigId" | "value" | "sourceUrl" | "answerExplanation"
	> &
		Partial<ColumnValueInfo>
) => {
	if (!Array.isArray(person.columns)) {
		person.columns = [];
	}

	const columnIndex = person.columns.findIndex(
		(column) => column.columnConfigId === columnValue.columnConfigId
	);

	if (columnIndex > -1) {
		person.columns[columnIndex] = {
			...person.columns[columnIndex],
			value: columnValue.value,
			sourceUrl: columnValue.sourceUrl,
			answerExplanation: columnValue.answerExplanation,
		};
	} else {
		person.columns.push({
			...columnValue,
			listPersonId,
		} as (typeof person.columns)[number]);
	}
};

export const listSlice = createSlice({
	name: "list",
	initialState,
	reducers: {
		setCurrentLeadIndex: (state, { payload }) => {
			state.currentLeadIndex = payload;
		},
		setLeavingInboxLead: (state, { payload }: PayloadAction<number | null>) => {
			state.leavingInboxLeadId = payload;
		},
		setCurrentSearchResultsIndex: (state, { payload }) => {
			state.currentSearchResultsIndex = payload;
		},
		setListSorting: (state, { payload }) => {
			if (
				state.sortingList.sortOrder === Order.DESC &&
				payload.sortOrder === Order.ASC &&
				payload.sortBy === state.sortingList.sortBy
			) {
				payload.sortBy = initialState.sortingList.sortBy;
				payload.sortOrder = initialState.sortingList.sortOrder;
			}

			state.sortingList = payload;
		},
		setInboxSorting: (state, { payload }) => {
			state.sortingInbox = resolveInboxSorting(state.sortingInbox, payload);
		},
		// Set the inbox sort exactly as given (no 3-state reset). Mirrors the URL-driven sort into
		// state so the data-loads stay in sync with the URL source of truth (PREC-3544).
		applyInboxSorting: (state, { payload }) => {
			state.sortingInbox = payload;
		},
		removeEveryRecordAbove15: (state) => {
			state.inbox = state.inbox.slice(0, 15);
		},
		clearInbox: (state, { payload }: PayloadAction<object | undefined>) =>
			applyInboxCleared(state, payload),
		releaseInboxScope: (state) => applyInboxScopeReleased(state),
		clearCampaign: (state) => {
			state.campaign = [];
		},
		clearLists: (state) => {
			state.lists = [];
		},
		addEmailData: (state, { payload }) => applyLeadContactFound(state, payload),
		removeFromInbox: (state, { payload }: PayloadAction<number>) => {
			removeLeadFromInboxState(state, payload);
		},
		updateDeepResearchResponseInInbox: (
			state,
			{
				payload,
			}: PayloadAction<{
				prospectId: number;
				deepResearchResponseId: number;
				response: DeepResearchResponseData;
				fitAssessment?: string | null;
				fitReason?: string | null;
			}>
		) => {
			const index = state.inbox.findIndex((person) => person.prospect?.id === payload.prospectId);

			if (index === -1 || !state.inbox[index].prospect) {
				return;
			}

			state.inbox[index].prospect!.deepResearchResponse = {
				id: payload.deepResearchResponseId,
				response: payload.response,
				fitAssessment: payload.fitAssessment ?? null,
				fitReason: payload.fitReason ?? null,
			};
		},
		updateOrganizationContact: (
			state,
			{
				payload,
			}: PayloadAction<{
				organizationId: number;
				email: string | null;
				phone: string | null;
				listPersonIds: number[];
			}>
		) => {
			for (const listPersonId of payload.listPersonIds) {
				const index = state.inbox.findIndex((p) => p.id === listPersonId);

				if (index === -1 || !state.inbox[index].prospect?.organization) {
					continue;
				}

				if (payload.email !== null) {
					state.inbox[index].prospect!.organization!.email = payload.email;
				}

				if (payload.phone !== null) {
					state.inbox[index].prospect!.organization!.phone = payload.phone;
				}
			}
		},
		applyCrmProspectUpdate: (state, { payload }: PayloadAction<CrmProspectUpdate>) => {
			applyCrmUpdateToListPersons(state.inbox, payload);
			applyCrmUpdateToListPersons(state.searchResults, payload);
			applyCrmUpdateToListPersons(state.campaign, payload);
		},
		updatePersonInInbox: (
			state,
			{
				payload,
			}: PayloadAction<{
				personId: number;
				updatedPerson: ListPerson;
			}>
		) => {
			const personIndex = state.inbox.findIndex((p) => p.id === payload.personId);

			if (personIndex !== -1) {
				state.inbox[personIndex] = payload.updatedPerson;
			}
		},
		updateProspectGenderInInbox: (
			state,
			{
				payload,
			}: PayloadAction<{
				listPersonId: number;
				gender: string | null;
			}>
		) => {
			const personIndex = state.inbox.findIndex((p) => p.id === payload.listPersonId);

			if (personIndex === -1) {
				return;
			}

			const person = state.inbox[personIndex];

			if (!person.prospect) {
				return;
			}

			person.prospect.gender = payload.gender;
			person.prospect.genderProbability = payload.gender ? 100 : null;
			state.inbox[personIndex] = person;
		},
		updateCopywritingInInbox: (
			state,
			{
				payload,
			}: PayloadAction<{
				listPersonId: number;
				copywritingStatus?: CopywritingStatus | null;
				copywritingOutput?: GenerateCopywritingResponse | null;
				copywritingEventId?: number | null;
				copywritingError?: string | null;
			}>
		) => {
			const index = state.inbox.findIndex((p) => p.id === payload.listPersonId);

			if (index === -1) {
				return;
			}

			const person = state.inbox[index];

			if (payload.copywritingStatus !== undefined) {
				person.copywritingStatus = payload.copywritingStatus;
			}

			if (payload.copywritingOutput !== undefined) {
				person.copywritingOutput = payload.copywritingOutput;
			}

			if (payload.copywritingEventId !== undefined) {
				person.copywritingEventId = payload.copywritingEventId;
			}

			if (payload.copywritingError !== undefined) {
				person.copywritingError = payload.copywritingError;
			}

			state.inbox[index] = person;
		},
		setListPersonAsyncStatus: (
			state,
			{
				payload,
			}: PayloadAction<{
				listPersonId: number;
				asyncProcessingType: AsyncProcessingType;
				asyncProcessingStatus: AsyncProcessingStatus;
			}>
		) => {
			const index = state.inbox.findIndex((p) => p.id === payload.listPersonId);

			if (index === -1) {
				return;
			}

			state.inbox[index].asyncProcessingType = payload.asyncProcessingType;
			state.inbox[index].asyncProcessingStatus = payload.asyncProcessingStatus;
		},
		upsertListPersonColumnValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				listPersonId: number;
				columnValue: ColumnValueInfo;
			}>
		) => {
			const index = state.inbox.findIndex((p) => p.id === payload.listPersonId);

			if (index === -1) {
				return;
			}

			upsertColumnValueOnPerson(state.inbox[index], payload.listPersonId, payload.columnValue);
		},
		removeTrackerSignalResultFromInbox: (
			state,
			{
				payload,
			}: PayloadAction<{
				listPersonId?: number;
				trackerSignalResultId: number;
			}>
		) => {
			const listPersons = payload.listPersonId
				? state.inbox.filter((person) => person.id === payload.listPersonId)
				: state.inbox;

			for (const person of listPersons) {
				if (person.trackerSignalResult?.id === payload.trackerSignalResultId) {
					person.trackerSignalResult = null;
				}

				const trackerSignalResults = person.prospect?.organization?.trackerSignalResults;

				if (!trackerSignalResults) {
					continue;
				}

				person.prospect!.organization!.trackerSignalResults = trackerSignalResults.filter(
					(signal) => signal.id !== payload.trackerSignalResultId
				);
			}
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(listApi.endpoints.getLists.matchFulfilled, (state, { payload }) => {
			if (!payload) {
				return;
			}

			if (payload.data) {
				state.lists = payload.data;
			}

			if (payload.meta) {
				state.listMeta = payload.meta;
			}
		});
		builder.addMatcher(listApi.endpoints.getList.matchFulfilled, (state, { payload }) => {
			applyGetListFulfilled(state, payload);
		});
		// Overview lifecycle (see ./list-lean-lists.helper): only the overview's own requests (the
		// `scope` marker) claim/replace/empty the rows; latest response wins (PREC-4131, PREC-4892).
		addLeanListsOverviewMatchers(builder);
		// Inbox lifecycle (see ./list-inbox.helper): pending claims the inbox by request id, fulfilled
		// applies only the latest response so a stale tab response can't clobber the active tab's rows.
		builder.addMatcher(listApi.endpoints.getUnassigned.matchPending, (state, { meta }) =>
			applyGetUnassignedPending(state, meta.requestId)
		);
		builder.addMatcher(listApi.endpoints.getUnassigned.matchFulfilled, (state, { payload, meta }) =>
			applyGetUnassignedFulfilled(state, payload, meta.requestId)
		);
		builder.addMatcher(listApi.endpoints.getAllPersons.matchFulfilled, (state, { payload }) =>
			applyGetAllPersonsFulfilled(state, payload)
		);
		// The campaign builder's lead panel: same lifecycle, so an empty list never keeps or gets back
		// the previous list's lead from a late answer (PREC-4790).
		builder.addMatcher(listApi.endpoints.getUnassignedAndDummy.matchPending, (state, { meta }) =>
			applyGetUnassignedAndDummyPending(state, meta.requestId)
		);
		builder.addMatcher(listApi.endpoints.getUnassignedAndDummy.matchFulfilled, (state, action) =>
			applyGetUnassignedAndDummyFulfilled(state, action)
		);
		// getAllProspects inbox lifecycle (see ./list-inbox.helper): the Messages surfaces all populate
		// the inbox through this one paginated query. Pending claims it by request id (clearing only on
		// page 1); fulfilled applies only the latest response so a stale tab's request can't clobber the
		// active tab's rows — which lets the components drop their manual lazy-query abort() dance.
		builder.addMatcher(listApi.endpoints.getAllProspects.matchPending, (state, { meta }) =>
			applyGetAllProspectsPending(state, meta.requestId, meta.arg.originalArgs?.page)
		);
		builder.addMatcher(
			listApi.endpoints.getAllProspects.matchFulfilled,
			(state, { payload, meta }) => applyGetAllProspectsFulfilled(state, payload, meta.requestId)
		);
		// A rejected latest request: the surface reports the failed load instead of an empty list
		// (PREC-5088). Stale, aborted and skipped requests stay silent.
		builder.addMatcher(listApi.endpoints.getAllProspects.matchRejected, (state, { meta }) =>
			applyGetAllProspectsRejected(state, meta)
		);

		builder.addMatcher(
			conversationApi.endpoints.updateConversationSentiment.matchFulfilled,
			(state, { payload }) => {
				const index = state.inbox.findIndex((person) => person.id === payload.listPersonId);

				if (index === -1) {
					return;
				}

				if (state.inbox[index]?.prospect && payload.sentiment && payload.wasLastConversation) {
					state.inbox[index]!.prospect!.sentiment = payload.sentiment;
				}

				if (
					state.inbox[index] &&
					state.inbox[index]?.prospect &&
					state.inbox[index].prospect?.conversations &&
					state.inbox[index].prospect?.conversations?.length &&
					payload.sentiment &&
					payload.id
				) {
					const alteredConversations =
						state.inbox[index]!.prospect!.conversations!.map((conversation: Conversation) => {
							if (conversation.id === payload.id) {
								return {
									...conversation,
									sentiment: payload.sentiment,
								};
							}

							return conversation;
						}) ?? [];

					state.inbox[index]!.prospect!.conversations =
						alteredConversations as unknown as Conversation[];
				}
			}
		);

		builder.addMatcher(listApi.endpoints.getSingleListPerson.matchFulfilled, (state, { payload }) =>
			applySingleListPersonToInbox(state, payload)
		);

		builder.addMatcher(listApi.endpoints.updateCostumColumn.matchFulfilled, (state, action) => {
			const index = state.inbox.findIndex((person) => person.id === action.payload.id);

			const person = state.inbox[index];

			if (index === -1) {
				return;
			}

			const columnIndex = person.columns.findIndex(
				(column) => column.columnConfigId === action.payload.columnValue.columnConfigId
			);

			if (columnIndex > -1) {
				const column = person.columns[columnIndex];

				column.value = action.payload.columnValue.value;

				state.inbox[index].columns[columnIndex] = column;
			} else {
				state.inbox[index].columns.push(action.payload.columnValue);
			}
		});
		builder.addMatcher(listApi.endpoints.updateListPerson.matchFulfilled, (state, action) => {
			const index = state.inbox.findIndex((person) => person.id === action.payload.id);

			if (index === -1) {
				return;
			}

			state.inbox[index].campaignConfig = action.payload.campaignConfig;
			state.inbox[index].campaignId = action.payload.campaignId;
		});

		builder.addMatcher(
			listApi.endpoints.updateListPersonSenders.matchFulfilled,
			(state, action) => {
				applyListPersonSendersUpdate(state, action.payload);
			}
		);

		builder.addMatcher(
			listApi.endpoints.updateListPersonCampaign.matchFulfilled,
			(state, action) => {
				applyListPersonCampaignUpdate(state, action.payload);
			}
		);

		builder.addMatcher(
			listApi.endpoints.updateCopywritingOutput.matchFulfilled,
			(state, { payload }) => {
				const index = state.inbox.findIndex((person) => person.id === payload.id);

				if (index === -1) {
					return;
				}

				state.inbox[index].copywritingOutput = payload.copywritingOutput;
			}
		);

		builder.addMatcher(scheduleApi.endpoints.sendMessage.matchFulfilled, (state, { payload }) => {
			removeLeadFromInboxState(state, payload.listPersonId ?? -1);
		});

		// NOTE: sendMessagesDeferred (undo send) intentionally has NO matcher here —
		// the lead is removed explicitly via `removeFromInbox` when the undo window
		// elapses, not when the API accepts the deferred send.
		builder.addMatcher(scheduleApi.endpoints.sendMessages.matchFulfilled, (state, action) => {
			removeLeadFromInboxState(state, action.meta.arg.originalArgs.listPersonId);
		});

		builder.addMatcher(
			scheduleApi.endpoints.sendFollowUpMessages.matchFulfilled,
			(state, { payload }) => {
				removeLeadFromInboxState(state, payload?.[0]?.listPersonId ?? -1);
			}
		);

		builder.addMatcher(listApi.endpoints.updateProspect.matchFulfilled, (state, action) => {
			applyProspectUpdateToRows([state.inbox, state.searchResults, state.campaign], action.payload);
		});

		builder.addMatcher(
			listApi.endpoints.updateProspectPendingEmail.matchFulfilled,
			(state, action) => {
				const {
					id,
					action: pendingEmailAction,
					email,
					emailStatus,
					pendingEmails,
				} = action.payload;

				const applyToList = (listPersons: ListPerson[]) => {
					const person = listPersons.find((p) => p.id === +id);

					if (!person?.prospect) {
						return;
					}

					if (pendingEmailAction === "CONFIRM") {
						// Confirm makes the candidate the primary email.
						person.prospect.email = email ?? person.prospect.email;
						person.prospect.emailStatus = emailStatus ?? person.prospect.emailStatus;
					}

					// The hover's facts follow the address the answer names (PREC-4262).
					mergeEmailFacts(person, action.payload);

					// The backend returns the remaining candidates (a previous primary may now appear
					// here so it can be switched back); mirror that list exactly.
					person.prospect.pendingEmails = pendingEmails;
				};

				applyToList(state.inbox);
				applyToList(state.searchResults);
				applyToList(state.campaign);
			}
		);

		builder.addMatcher(
			listApi.endpoints.updateLeadScoringResult.matchFulfilled,
			(state, action) => {
				const index = state.inbox.findIndex((person) => person.id === +action.payload.id);

				if (index === -1) {
					return;
				}

				const person = state.inbox[index];

				if (action.payload.updateProspectDto.scorePersonaCategoryResult && person.prospect) {
					person.prospect.scorePersonaCategoryResult =
						action.payload.updateProspectDto.scorePersonaCategoryResult;
				}

				if (
					action.payload.updateProspectDto.scoreCompanyCategoryResult &&
					person?.prospect?.organization?.scoreCompanyCategoryResult
				) {
					person.prospect.organization.scoreCompanyCategoryResult.result =
						action.payload.updateProspectDto.scoreCompanyCategoryResult.result;
				}

				state.inbox[index] = person;
			}
		);

		builder.addMatcher(listApi.endpoints.updateMapBusiness.matchFulfilled, (state, action) => {
			const index = state.inbox.findIndex((person) => person.id === +action.payload.id);

			if (index === -1) {
				return;
			}

			const person = state.inbox[index];

			if (action.payload.updateMapBusinessDto.name && person.mapBusiness) {
				person.mapBusiness.name = action.payload.updateMapBusinessDto.name;
			}

			state.inbox[index] = person;
		});
		builder.addMatcher(listApi.endpoints.addPersonToList.matchFulfilled, (state, action) => {
			const newPerson = action.payload.data?.[0];

			if (!newPerson) {
				return;
			}

			// if already in inbox, just focus the existing person instead of adding a duplicate
			const existingIndex = state.inbox.findIndex((person) => person.id === newPerson.id);

			if (existingIndex !== -1) {
				state.inbox[existingIndex] = newPerson as ListPerson;
				state.currentLeadIndex = existingIndex;

				return;
			}

			state.inbox = [newPerson, ...state.inbox];
			state.currentLeadIndex = 0;
		});
		builder.addMatcher(listApi.endpoints.updateOrganization.matchFulfilled, (state, action) => {
			const index = state.inbox.findIndex((person) => person.id === +action.payload.id);

			if (index === -1) {
				return;
			}

			const person = state.inbox[index];

			if (action.payload.updateProspectDto.name && person?.prospect?.organization?.name) {
				person.prospect.organization.name = action.payload.updateProspectDto.name;
			}

			state.inbox[index] = person;
		});
		builder.addMatcher(listApi.endpoints.getLeanList.matchFulfilled, (state, { payload }) => {
			if (!payload?.id) {
				return;
			}

			const index = state.lists.findIndex((list) => list.id === payload.id);

			if (index !== -1) {
				state.lists[index] = {
					...state.lists[index],
					...payload,
				};
			}

			state.currentList = payload;

			state.inbox.forEach((person, i) => {
				if (person.list?.id === payload.id) {
					state.inbox[i].list = {
						...state.inbox[i].list,
						...payload,
					};
				}
			});
		});
		builder.addMatcher(listApi.endpoints.updateList.matchFulfilled, (state, action) => {
			if (!action.payload.id) {
				return;
			}

			const index = state.lists.findIndex((list) => list.id === action.payload.id);

			if (index !== -1) {
				state.lists[index] = {
					...state.lists[index],
					...action.payload,
				};
				state.currentList = state.lists[index];
			} else {
				state.currentList = action.payload as unknown as List;
			}

			state.inbox.forEach((person, i) => {
				if (person.list?.id === action.payload.id) {
					state.inbox[i].list = {
						...state.inbox[i].list,
						...action.payload,
					};
				}
			});
		});

		builder.addMatcher(scheduleApi.endpoints.sendMessage.matchFulfilled, (state, { payload }) => {
			const index = state.inbox.findIndex((person) => person.id === payload.listPersonId);

			if (index === -1 || !state.inbox[index].prospect) {
				return;
			}

			state.inbox[index]?.prospect?.conversations?.push(payload as Conversation);
			state.inbox[index] = state.inbox[index];
		});

		builder.addMatcher(scheduleApi.endpoints.toggleSequence.matchFulfilled, (state, { payload }) =>
			toggleInboxSchedules(state.inbox, payload)
		);

		// One schedule's new state, whichever write produced it (cancel, pause, resume, edit).
		builder.addMatcher(inboxSchedulePatchMatcher, (state, { payload }) =>
			patchInboxSchedule(state.inbox, payload)
		);

		builder.addMatcher(listApi.endpoints.deleteList.matchFulfilled, (state, action) => {
			if (!action.payload.id) {
				return;
			}

			const currentId = parseInt(action.payload.id?.toString());

			const index = state.lists.findIndex((list) => list.id === currentId);

			if (index === -1) {
				return;
			}

			state.lists = state.lists.filter((list) => list.id !== currentId);
		});

		builder.addMatcher(listApi.endpoints.searchForPhones.matchFulfilled, (state, action) => {
			for (const person of action.payload) {
				const index = state.inbox.findIndex((p) => p.id === person.id);

				if (index === -1) {
					continue;
				}

				if (state.inbox[index]?.prospect !== undefined) {
					setProspectEmailStatus(index, person);
				}
			}

			function setProspectEmailStatus(index: number, person: { id: number; phoneStatus: string }) {
				const current = state.inbox[index].prospect;

				if (current) {
					current.phoneStatus = person.phoneStatus;

					state.inbox[index].prospect = current;
				}
			}
		});

		builder.addMatcher(listApi.endpoints.searchForEmails.matchFulfilled, (state, action) => {
			for (const person of action.payload) {
				const index = state.inbox.findIndex((p) => p.id === person.id);

				if (index === -1) {
					continue;
				}

				if (state.inbox[index].mapBusiness) {
					setMapBusinessEmailStatus(index, person);
				}

				if (state.inbox[index]?.offer !== undefined) {
					setOfferEmailStatus(index, person);
				}

				if (state.inbox[index]?.prospect !== undefined) {
					setProspectEmailStatus(index, person);
				}
			}

			function setProspectEmailStatus(index: number, person: { id: number; emailStatus: string }) {
				const current = state.inbox[index].prospect;

				if (current) {
					current.emailStatus = person.emailStatus;

					state.inbox[index].prospect = current;
				}
			}

			function setOfferEmailStatus(index: number, person: { id: number; emailStatus: string }) {
				const current = state.inbox[index].offer;

				if (current) {
					current.emailStatus = person.emailStatus;

					state.inbox[index].offer = current;
				}
			}

			function setMapBusinessEmailStatus(
				index: number,
				person: { id: number; emailStatus: string }
			) {
				const currentMapBusiness = state.inbox[index].mapBusiness;

				if (currentMapBusiness) {
					currentMapBusiness.emailStatus = person.emailStatus;

					state.inbox[index].mapBusiness = currentMapBusiness;
				}
			}
		});

		builder.addMatcher(
			listApi.endpoints.getScoreCompanyCategories.matchFulfilled,
			(state, { payload }) => {
				state.scoreCompanyCategories = payload;
			}
		);

		builder.addMatcher(
			listApi.endpoints.setScoreCompanyCategories.matchFulfilled,
			(state, { payload }) => {
				state.scoreCompanyCategories = payload;
			}
		);

		builder.addMatcher(
			hubspotApi.endpoints.createHubspotDeal.matchFulfilled,
			(state, { payload }) => {
				hydrateListPersonCollectionsWithCrmDeal(state, payload);
			}
		);

		builder.addMatcher(zohoApi.endpoints.createZohoDeal.matchFulfilled, (state, { payload }) => {
			hydrateListPersonCollectionsWithCrmDeal(state, payload);
		});

		builder.addMatcher(
			pipedriveApi.endpoints.createPipedriveDeal.matchFulfilled,
			(state, { payload }) => {
				hydrateListPersonCollectionsWithCrmDeal(state, payload);
			}
		);

		builder.addMatcher(
			copywritingOrchestrationApi.endpoints.generateCopywriting.matchFulfilled,
			(state, { payload }) => {
				const index = state.inbox.findIndex((p) => p.id === payload.listPersonId);

				if (index === -1) {
					return;
				}

				const person = state.inbox[index];

				person.copywritingStatus = CopywritingStatus.QUEUED;
				person.copywritingEventId = payload.eventId;
				person.copywritingOutput = null;
				person.copywritingError = null;
				state.inbox[index] = person;
			}
		);

		builder.addMatcher(
			copywritingOrchestrationApi.endpoints.generateBatchCopywriting.matchFulfilled,
			(state, { payload }) => {
				for (const accepted of payload.accepted) {
					const index = state.inbox.findIndex((p) => p.id === accepted.listPersonId);

					if (index === -1) {
						continue;
					}

					const person = state.inbox[index];

					person.copywritingStatus = CopywritingStatus.QUEUED;
					person.copywritingEventId = accepted.eventId;
					person.copywritingOutput = null;
					person.copywritingError = null;
					state.inbox[index] = person;
				}
			}
		);

		builder.addMatcher(listApi.endpoints.bulkRunPrompt.matchFulfilled, (state, action) => {
			const arg = action.meta.arg.originalArgs;
			const ids = arg.selectType === "ALL" ? null : (arg.ids ?? []);

			for (let i = 0; i < state.inbox.length; i++) {
				const person = state.inbox[i];

				if (person.list?.id !== arg.listId) {
					continue;
				}

				if (ids !== null && !ids.includes(person.id)) {
					continue;
				}

				state.inbox[i].asyncProcessingType = AsyncProcessingType.AI_SNIPPET;
				state.inbox[i].asyncProcessingStatus = AsyncProcessingStatus.PENDING;
			}
		});

		builder.addMatcher(columnApi.endpoints.runPrompt.matchFulfilled, (state, action) => {
			const listPersonId = action.meta.arg.originalArgs.listPersonId;
			const columnValues = action.payload?.columnValues;

			if (!Array.isArray(columnValues) || columnValues.length === 0) {
				return;
			}

			const index = state.inbox.findIndex((person) => person.id === listPersonId);

			if (index === -1) {
				return;
			}

			const person = state.inbox[index];

			for (const cv of columnValues) {
				upsertColumnValueOnPerson(person, listPersonId, cv);
			}
		});
	},
});

export const {
	setListSorting,
	removeEveryRecordAbove15,
	setInboxSorting,
	applyInboxSorting,
	clearInbox,
	releaseInboxScope,
	clearLists,
	setCurrentLeadIndex,
	setLeavingInboxLead,
	setCurrentSearchResultsIndex,
	addEmailData,
	applyCrmProspectUpdate,
	removeFromInbox,
	updateDeepResearchResponseInInbox,
	updateOrganizationContact,
	updatePersonInInbox,
	updateProspectGenderInInbox,
	updateCopywritingInInbox,
	removeTrackerSignalResultFromInbox,
	setListPersonAsyncStatus,
	upsertListPersonColumnValue,
} = listSlice.actions;
export const listReducer = listSlice.reducer;
export const listSelector = (state: RootState) => state[listSlice.name];
